/**
 * Shared timezone-aware date/time formatting for the UI.
 * Locale defaults to the browser (undefined → Intl default).
 */

/** If ISO string has no Z or offset, treat as UTC (API convention). */
export function normalizeTimestampForParse(s: string): string {
  const t = s.trim();
  if (!t) return t;
  if (t.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(t)) return t;
  return `${t}Z`;
}

export function parseToDate(input: string | number | Date | undefined | null): Date | null {
  if (input === undefined || input === null) return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === 'number') {
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(input).trim();
  if (!s) return null;
  const d = new Date(normalizeTimestampForParse(s));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(
  input: string | number | Date | undefined | null,
  timeZone: string,
  locale?: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = parseToDate(input);
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { timeZone, ...options }).format(d);
  } catch {
    return '—';
  }
}

const UI_DATETIME: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

/** Full date+time in user locale, in the selected IANA timezone. */
export function formatUiDateTime(
  input: string | number | Date | undefined | null,
  timeZone: string,
  locale?: string
): string {
  return formatDateTime(input, timeZone, locale, UI_DATETIME);
}

/** Date only (e.g. table “created” columns). */
export function formatUiDateOnly(
  input: string | number | Date | undefined | null,
  timeZone: string,
  locale?: string
): string {
  return formatDateTime(input, timeZone, locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Short time (hour + minute), 24-hour clock. */
export function formatUiTimeShort(
  input: string | number | Date | undefined | null,
  timeZone: string,
  locale?: string
): string {
  return formatDateTime(input, timeZone, locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Time with seconds (scenario row labels, logs), 24-hour clock. */
export function formatUiTimeWithSeconds(
  input: string | number | Date | undefined | null,
  timeZone: string,
  locale?: string
): string {
  return formatDateTime(input, timeZone, locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** e.g. “Jan 5, 2025” — replaces dayjs MMM D, YYYY in selected zone. */
export function formatUiMonthDayYear(
  input: string | number | Date | undefined | null,
  timeZone: string,
  locale?: string
): string {
  return formatDateTime(input, timeZone, locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** 24-hour axis tick and chart tooltip time (selected TZ). */
export function formatChartAxisTime(
  input: string | number | Date | undefined | null,
  timeZone: string,
  locale?: string
): string {
  return formatDateTime(input, timeZone, locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Runbook timeframe summary strings. */
export function formatTimeframeRange(
  start: string | undefined,
  end: string | undefined,
  timeZone: string,
  locale?: string
): string {
  if (!start && !end) return 'Not set';
  if (!start) return `Until ${end ? formatUiDateTime(end, timeZone, locale) : '—'}`;
  if (!end) return `From ${formatUiDateTime(start, timeZone, locale)}`;
  return `${formatUiDateTime(start, timeZone, locale)} – ${formatUiDateTime(end, timeZone, locale)}`;
}

/**
 * Current UTC offset for an IANA zone at `at`, e.g. "UTC+05:30" or "UTC-05:00".
 * Uses Intl longOffset when available; normalizes "GMT…" to "UTC…".
 */
export function formatUtcOffsetLabel(timeZone: string, at: Date = new Date()): string {
  const normalize = (raw: string | undefined): string | null => {
    if (!raw) return null;
    let s = raw.trim().replace(/\u2212/g, '-');
    if (/^GMT/i.test(s)) {
      s = 'UTC' + s.slice(3);
    }
    if (/^UTC$/i.test(s)) {
      return 'UTC+00:00';
    }
    if (/^UTC[+-]/i.test(s)) {
      return s;
    }
    return s;
  };

  const tryParts = (timeZoneName: Intl.DateTimeFormatOptions['timeZoneName']): string | null => {
    try {
      const dtf = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        timeZoneName,
      });
      const raw = dtf.formatToParts(at).find((p) => p.type === 'timeZoneName')?.value;
      return normalize(raw);
    } catch {
      return null;
    }
  };

  return (
    tryParts('longOffset') ??
    tryParts('shortOffset') ??
    (timeZone === 'UTC' || timeZone === 'Etc/UTC' || timeZone === 'Etc/GMT' ? 'UTC+00:00' : '—')
  );
}

/** Intl "short" often returns GMT+2 / UTC+5 — treat as offset-only, not a city abbreviation. */
function isOffsetStyleAbbrev(s: string): boolean {
  const t = s.trim().replace(/\u2212/g, '-');
  if (/^GMT$/i.test(t) || /^UTC$/i.test(t)) return true;
  if (/^GMT[+-]/i.test(t)) return true;
  if (/^UTC[+-]/i.test(t)) return true;
  return false;
}

function getTimeZoneLongName(timeZone: string, at: Date): string | null {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'long',
    });
    return dtf.formatToParts(at).find((p) => p.type === 'timeZoneName')?.value?.trim() ?? null;
  } catch {
    return null;
  }
}

/**
 * Short display name (EST, CET, India IST, Israel IST/IDT). Never returns GMT±… — offsets belong in {@link formatUtcOffsetLabel} only (UTC).
 */
export function formatTimeZoneAbbreviation(timeZone: string, at: Date = new Date()): string {
  if (timeZone === 'UTC' || timeZone === 'Etc/UTC' || timeZone === 'Etc/GMT') {
    return 'UTC';
  }

  // India (IANA uses Kolkata; search "India" finds this via getTimezoneSearchHaystack)
  if (timeZone === 'Asia/Kolkata' || timeZone === 'Asia/Calcutta') {
    return 'IST';
  }

  // Israel: Intl "short" is often GMT+2/3; use long name for IST vs IDT
  if (timeZone === 'Asia/Jerusalem' || timeZone === 'Asia/Tel_Aviv') {
    const longName = getTimeZoneLongName(timeZone, at);
    if (longName) {
      if (/\b(daylight|summer)\b/i.test(longName)) return 'IDT';
      return 'IST';
    }
  }

  const tryStyle = (timeZoneName: Intl.DateTimeFormatOptions['timeZoneName']): string | null => {
    try {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName,
      });
      const raw = dtf.formatToParts(at).find((p) => p.type === 'timeZoneName')?.value?.trim();
      return raw && raw.length > 0 ? raw : null;
    } catch {
      return null;
    }
  };

  const pick = tryStyle('short') ?? tryStyle('shortGeneric');
  if (!pick || isOffsetStyleAbbrev(pick)) {
    return '—';
  }
  // Normalize lone GMT label to UTC (offset line will show UTC+00:00)
  if (/^GMT$/i.test(pick)) return '—';
  return pick;
}

/** True when abbrev and offset carry the same information (e.g. GMT+5 vs UTC+05:00, or GMT vs UTC+00:00). */
function abbrevRedundantWithOffset(abbrev: string, offset: string): boolean {
  const norm = (s: string) =>
    s
      .replace(/\u2212/g, '-')
      .replace(/\s/g, '')
      .replace(/^GMT/i, 'UTC')
      .toUpperCase();
  const a = norm(abbrev);
  const b = norm(offset);
  if (a === b) return true;
  if (a === 'UTC' && b === 'UTC+00:00') return true;
  // Both numeric offsets only
  if (/^UTC[+-]\d/.test(a) && /^UTC[+-]\d/.test(b)) {
    return a.replace(/:/g, '') === b.replace(/:/g, '') || a === b;
  }
  return false;
}

/**
 * Combined label for profile/picker: "EST, UTC-05:00" or "IST, UTC+05:30".
 * Omits duplicate when abbrev is already an offset string matching UTC offset.
 */
export function formatTimezoneAbbrevAndOffset(timeZone: string, at: Date = new Date()): string {
  const abbrev = formatTimeZoneAbbreviation(timeZone, at);
  const offset = formatUtcOffsetLabel(timeZone, at);
  if (abbrev === '—' && offset === '—') return '—';
  if (abbrev === '—') return offset;
  if (offset === '—') return abbrev;
  if (abbrevRedundantWithOffset(abbrev, offset)) return offset;
  return `${abbrev}, ${offset}`;
}

/** Extra tokens so picker search finds zones by country/city (e.g. "india" → Asia/Kolkata). */
const TIMEZONE_SEARCH_ALIASES: Record<string, string> = {
  'Asia/Kolkata':
    'india indian bharat mumbai bombay delhi kolkata calcutta bangalore bengaluru chennai madras hyderabad pune ahmedabad surat jaipur india standard',
  'Asia/Calcutta': 'india indian kolkata calcutta',
  'Asia/Jerusalem': 'israel jerusalem tel aviv yafo haifa beer sheva beersheba idt ist',
  'Asia/Tel_Aviv': 'israel jerusalem tel aviv',
};

/**
 * IANA id plus alias words for Autocomplete filtering (lowercased by caller).
 */
export function getTimezoneSearchHaystack(ianaId: string): string {
  const extra = TIMEZONE_SEARCH_ALIASES[ianaId];
  return extra ? `${ianaId} ${extra}` : ianaId;
}

export function isValidIanaTimeZone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function getBrowserDefaultTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Sorted IANA zones for the picker; falls back if supportedValuesOf is missing. */
export function getAllIanaTimeZones(): string[] {
  try {
    const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf;
    if (typeof supportedValuesOf === 'function') {
      const values = supportedValuesOf.call(Intl, 'timeZone');
      if (Array.isArray(values) && values.length > 0) {
        return [...values].sort((a, b) => a.localeCompare(b));
      }
    }
  } catch {
    /* ignore */
  }
  return [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Jerusalem',
    'Asia/Kolkata',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
  ];
}
